import { get, post, put } from 'api/apiRequest';
import { sanitize } from 'dompurify';

export interface SettlementToken {
  name: string;
  displayName: string;
  tooltip: string;
  canUseInSubject: boolean;
  expression: string;
}

export type SettlementTemplateEmailName =
  | 'AwaitingClientReview'
  | 'AwaitingCounterpartyReview'
  | 'DeclinedByCounterparty'
  | 'AcceptedByCounterparty'
  | 'SettlementExecuted'
  | 'ClientAccountCreation'
  | 'AwaitingCounterpartyReviewReminder';

export interface SettlementEmailTemplate {
  id: number;
  name: SettlementTemplateEmailName;
  useAdditionalRecipients: boolean;
  displayName: string;
  includeAdminsToRecipients: boolean;
  includeIndividualsToRecipients: boolean;
  subject: string;
  template: string;
  tokens: Array<SettlementToken>;
}

export interface SettlementEmailTemplateDefault {
  defaultTemplate: string;
  defaultSubject: string;
}

export const settlementEmailTemplates = {
  get: async () => {
    const result = await get<SettlementEmailTemplate[]>(
      'v1/SettlementEmailTemplates',
    );
    return result?.map(sanitizeTemplate);
  },
  getDefaultTemplate: async (id: number) => {
    const result = await get<SettlementEmailTemplateDefault>(
      `v1/SettlementEmailTemplates/${id}`,
    );

    //api return a lot of redundant properties
    const resultDefault: SettlementEmailTemplateDefault | undefined = result && {
      defaultSubject: result.defaultSubject,
      defaultTemplate: result.defaultTemplate,
    };

    return sanitizeDefaultTemplate(resultDefault);
  },
  put: (id: number, data: SettlementEmailTemplate) =>
    put(`v1/SettlementEmailTemplates/${id}`, data),
  resetDefault: (id: number) =>
    post<SettlementEmailTemplate>(`v1/SettlementEmailTemplates/${id}/resetdefault`),
};

/**
 * Sanitizer, to prevent XSS attacks.
 */
const sanitizeTemplate = <T extends SettlementEmailTemplate | undefined>(
  item: T,
): T => {
  if (!item) return item;
  const result: T = {
    ...item,
    subject: sanitize(item.subject),
    template: sanitize(item.template),
  };
  return result;
};

const sanitizeDefaultTemplate = (
  item: SettlementEmailTemplateDefault | undefined,
) => {
  if (!item) return item;
  const result: SettlementEmailTemplateDefault = {
    ...item,
    defaultSubject: sanitize(item.defaultSubject),
    defaultTemplate: sanitize(item.defaultTemplate),
  };
  return result;
};
